import {
  AttachmentRequestRecord,
  ClusterDetachRecord,
  ConnectionRecord,
  ContentNotificationRecord,
  NodeStateRecord,
  PeerNotificationRecord,
  PeerRequestRecord,
  TransferRecord
} from '../../../webrtc/agent/interfaces';
import { BitrateChange, EventReport } from '../events/interfaces';
import ContentConfig from '../../../config/ContentConfig';
import DataReportBlock from '../../../reporter/report/DataReportBlock';
import DataReportMap, {
  getDataReportMap
} from '../../../reporter/report/DataReportMap';
import LogReport from './LogReport';
import OverflowData from './OverflowData';
import MaximumReportConfig from '../../../config/MaximumReportConfig';
import logger from '../../../log';
import { DetectionEvent, PrimingEvent } from '../../../reporter/constants';
import {
  Ecdn11Block,
  GeneralBlock,
  Report
} from '../../../reporter/report/Report';
import { LogReporterMessage } from './interfaces';
import { ReporterMessage } from '../../../log';
import { mergeReports } from '../../../common/Reporter/mergeReport';
import filterConnections from './filterConnections';
import filterTransfers from './filterTransfers';

const log = logger.getLogger(__filename);

type LogDataReportMap = DataReportMap<
  ReporterMessage,
  LogReport,
  LogReporterMessage
>;
type LogDataReportBlock = DataReportBlock<LogReporterMessage>;

function logToReport(
  data: LogDataReportMap | LogDataReportBlock
): LogDataReportBlock {
  return data instanceof DataReportMap ? data.toReport() : data;
}

export function checkForOverflow(config: ContentConfig, report: Report): void {
  if (report.log && config.report.max.log < report.log.data.length) {
    const length = report.log.data.length;
    const logs = report.log.data.splice(0, config.report.max.log - 1);
    logs.push({
      data: {
        count: 1,
        last: 1,
        msg: [`trimmed logs was ${length} trimmed to ${config.report.max.log}`],
        ts: Date.now()
      },
      level: 'INFO',
      type: 'OverflowData'
    });
    report.log.data = logs;
  }
}

export default class PendingReport {
  config: MaximumReportConfig;
  // A token is added in the formatReportRequest
  general: GeneralBlock;
  bitrates: DataReportBlock<BitrateChange>;
  detection: DataReportBlock<DetectionEvent>;
  events: DataReportBlock<EventReport>;
  log: DataReportMap<ReporterMessage, LogReport, LogReporterMessage>;
  overflows: OverflowData;
  priming: DataReportBlock<PrimingEvent>;
  ecdn11?: Ecdn11Block;
  transfers: DataReportBlock<TransferRecord>;
  connections: DataReportBlock<ConnectionRecord>;
  peerNotifications: DataReportBlock<PeerNotificationRecord>;
  contentNotifications: DataReportBlock<ContentNotificationRecord>;
  detachments: DataReportBlock<ClusterDetachRecord>;
  nodeState: DataReportBlock<NodeStateRecord>;
  peerRequests: DataReportBlock<PeerRequestRecord>;
  attachmentRequests: DataReportBlock<AttachmentRequestRecord>;

  constructor(item?: PendingReport) {
    const i = item || ({} as PendingReport);
    const config = i.config || new MaximumReportConfig();
    this.overflows = new OverflowData();
    this.config = config;
    this.attachmentRequests = i.attachmentRequests || new DataReportBlock();
    this.bitrates = i.bitrates || new DataReportBlock();
    this.connections = i.connections || new DataReportBlock();
    this.contentNotifications = i.contentNotifications || new DataReportBlock();
    this.detachments = i.detachments || new DataReportBlock();
    this.detection = i.detection || new DataReportBlock();
    this.ecdn11 = new Ecdn11Block(i.ecdn11);
    this.events = i.events || new DataReportBlock();
    this.general = new GeneralBlock(i.general);
    this.nodeState = i.nodeState || new DataReportBlock();
    this.peerNotifications = i.peerNotifications || new DataReportBlock();
    this.peerRequests = i.peerRequests || new DataReportBlock();
    this.priming = i.priming || new DataReportBlock();
    this.transfers = i.transfers || new DataReportBlock();

    this.log =
      i.log instanceof DataReportMap
        ? i.log
        : (this.log = getDataReportMap({
            max: config.log,
            name: 'log',
            overflowReporter: this.overflows.add,
            toStorage: LogReport.create
          }));

    // SRV-1703: Log message for failure to copy log into this structure.
    if (i.log && !(i.log instanceof DataReportMap)) {
      const msg = Object.entries(i.log).reduce((acc, [key, value]) => {
        acc[key] = typeof value;
        return acc;
      }, {} as Record<string, string>);
      log.warn('Unable to add existing logs, they are not of right type', msg);
    }
  }

  private toBasicReport() {
    return new Report({ ...this, log: logToReport(this.log) });
  }

  toReport(config: ContentConfig, reports?: Report[]): Report {
    const report = mergeReports([...(reports || []), this.toBasicReport()]);
    const context = config.reportingContext;
    checkForOverflow(config, report);
    report.log = this.config.log > 0 ? report.log : undefined;
    report.connections = filterConnections(report.connections, context);
    report.transfers = filterTransfers(report.transfers, context);
    return report;
  }
}
